import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaUser, FaMapMarkerAlt, FaGraduationCap, FaBriefcase,
  FaHeart, FaStar, FaCalendar, FaUsers, FaCheckCircle, FaClock, FaEye, FaShare,
  FaSpinner, FaArrowLeft, FaVenusMars, FaPray, FaLanguage,
  FaMoneyBillWave, FaRing, FaPhone, FaEnvelope, FaUserFriends,
  FaHome, FaBook, FaLaptop, FaChild, FaUtensils, FaGlassWhiskey,
  FaNotesMedical, FaTint, FaUniversity, FaBuilding, FaTag, FaUserTag,
  FaTransgender, FaWeight, FaRulerCombined, FaGlobe, FaHeartbeat,
  FaCheck, FaEyeSlash, FaLock, FaEdit, FaTimes,
} from 'react-icons/fa';
import {
  getPublicUserProfile,
  sendInterest,
  addToShortlist,
  getInterests,
  getShortlistedUsers,
  getUserActivePlans,
  checkContactPermission,
} from '../../Api/CoreApi';
import { handleShortlistUser, fetchShortlistedUsers, addShortlistListener } from '../../utils/shortlistUtils';
import { message, Modal, Form, Input, Select, InputNumber, Button, Space, Divider, Upload, DatePicker, Switch } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { BASE_API } from "../../Api/Api";

const { Option } = Select;
const { TextArea } = Input;

// ✅ Simple cache - same profile baar baar fetch na ho
const profileCache = new Map();

const UserProfileView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [shortlistedIds, setShortlistedIds] = useState([]);
  const [interestSentIds, setInterestSentIds] = useState([]);
  const [hiddenIds, setHiddenIds] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [activePlans, setActivePlans] = useState([]);
  const [canSeeContact, setCanSeeContact] = useState(false);
  const [contactCheckDone, setContactCheckDone] = useState(false);
  const [contactLimitReached, setContactLimitReached] = useState(false);

  // ✅ Edit Modal State
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);

  // ✅ Prevent double fetch (React Strict Mode)
  const fetchedRef = useRef(false);

  // ============================================
  // ✅ OPTIMIZED - Sab ek saath fetch karo
  // ============================================
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    if (!userId) {
      setError('No user ID provided');
      setLoading(false);
      return;
    }

    const loadAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("access_token");
        const currentUserId = localStorage.getItem('user_id');

        // ✅ Step 1: Profile fetch (cache check pehle)
        let profileData = null;

        if (profileCache.has(userId)) {
          profileData = profileCache.get(userId);
          console.log("⚡ Cache hit for:", userId);
        } else {
          try {
            const res = await fetch(
              `${BASE_API}/api/user/profile/${userId}/`,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            if (res.ok) {
              const data = await res.json();
              if (data && (data.firstname || data.username || data.user_id)) {
                profileData = data;
                profileCache.set(userId, data);
              }
            }
          } catch (err) {
            console.log("Primary API failed, trying fallback...");
          }

          if (!profileData) {
            try {
              const fallback = await getPublicUserProfile(userId);
              if (fallback) {
                profileData = fallback;
                profileCache.set(userId, fallback);
              }
            } catch (err) {
              console.log("Fallback also failed:", err.message);
            }
          }
        }

        if (!profileData) {
          setError('User not found');
          setLoading(false);
          return;
        }

        const actualUserId = profileData.user_id || userId;
        const isOwn = currentUserId && (
          String(currentUserId) === String(actualUserId) ||
          String(currentUserId) === String(userId)
        );

        console.log("🔍 Current User:", currentUserId);
        console.log("🔍 Profile User ID:", actualUserId);
        console.log("🔍 Is Own Profile:", isOwn);

        setUser({
          id: actualUserId,
          user_id: actualUserId,
          profile_id: profileData.profile_id || profileData.profileId || profileData.bid || '',
          username: profileData.username || '',
          firstname: profileData.firstname || profileData.first_name || profileData.name || 'User',
          gender: profileData.gender || '',
          age: profileData.age || 0,
          dob: profileData.dob || '',
          height: profileData.height || '',
          weight: profileData.weight || '',
          nationality: profileData.nationality || '',
          religion: profileData.religion || '',
          caste: profileData.caste || '',
          subcaste: profileData.subcaste || '',
          mother_tongue: profileData.mother_tongue || '',
          languages: profileData.languages || [],
          marrige_status: profileData.marrige_status || '',
          blood_group: profileData.blood_group || '',
          district: profileData.district || '',
          state: profileData.state || '',
          city: profileData.city || '',
          country: profileData.country || 'India',
          email: profileData.email || '',
          contact: profileData.contact || '',
          contact1: profileData.contact1 || '',
          smoking: profileData.smoking || '',
          drinking: profileData.drinking || '',
          bio: profileData.bio || '',
          hobbies: profileData.hobbies || [],
          interests: profileData.interests || [],
          father_name: profileData.father_name || '',
          mother_name: profileData.mother_name || '',
          father_occupation: profileData.father_occupation || '',
          mother_occupation: profileData.mother_occupation || '',
          brother: profileData.brother || 0,
          sister: profileData.sister || 0,
          brother_marrige: profileData.brother_marrige || 0,
          sister_marrige: profileData.sister_marrige || 0,
          about_family: profileData.about_family || '',
          university: profileData.university || '',
          course: profileData.course || '',
          sub_course: profileData.sub_course || '',
          job_title: profileData.job_title || profileData.occupation || '',
          job_type: profileData.job_type || '',
          company: profileData.company || '',
          annual_income: profileData.annual_income || 0,
          Lifestyle: profileData.Lifestyle || '',
          created_profile: profileData.created_profile || '',
          additional_notes: profileData.additional_notes || '',
          height_preference: profileData.height_preference || '',
          age_preference: profileData.age_preference || '',
          education_preference: profileData.education_preference || '',
          religion_preference: profileData.religion_preference || '',
          location_preference: profileData.location_preference || '',
          is_online: profileData.is_online || false,
          verified: profileData.verified || false,
          premium: profileData.premium || false,
          image: profileData.image || profileData.pic || '',
          pic: profileData.pic || '',
        });

        setIsCurrentUser(isOwn);

        if (isOwn) {
          setCanSeeContact(true);
          setContactCheckDone(true);
          await fetchInteractionsOnly();
        } else {
          await Promise.all([
            fetchInteractionsOnly(),
            fetchContactPermission(actualUserId),
          ]);
        }

      } catch (err) {
        console.error('Load error:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadAllData();

    const unsubscribe = addShortlistListener((newIds) => {
      setShortlistedIds(newIds);
    });
    return () => {
      if (unsubscribe) unsubscribe();
      fetchedRef.current = false;
    };
  }, [userId]);

  // ============================================
  // ✅ Interactions fetch - optimized
  // ============================================
  const fetchInteractionsOnly = async () => {
    try {
      const [shortlisted, interests, hidden] = await Promise.all([
        fetchShortlistedUsers().catch(() => []),
        getInterests().catch(() => []),
      ]);

      setShortlistedIds(shortlisted || []);
      setInterestSentIds((interests || []).map(item => item.to_user));
      setHiddenIds((hidden || []).map(item => item.target_user_id));
    } catch (error) {
      console.error("Interactions error:", error);
    }
  };

  // ============================================
  // ✅ Contact permission fetch
  // ============================================
  const fetchContactPermission = async (targetUserId) => {
    try {
      const plansData = await getUserActivePlans();
      const viewed = plansData.viewed_contacts || [];
      const plans = plansData.plans || [];

      setActivePlans(plans);

      console.log('📦 Active plans:', plans.length);
      console.log('👀 Viewed contacts:', viewed.length);

      // ⭐ Check if already viewed
      if (viewed.includes(targetUserId) || viewed.includes(String(targetUserId))) {
        setCanSeeContact(true);
        setContactCheckDone(true);
        return;
      }

      // ⭐ Check if any contact plan has remaining views (from ALL plans)
      const contactPlans = plans.filter(p => p.plan_type === 'contact_view');
      const totalRemaining = contactPlans.reduce((sum, p) => {
        if (p.contact_view_limit >= 9999) return 999999;
        return sum + (p.remaining_views || 0);
      }, 0);

      console.log('📊 Total remaining views:', totalRemaining);

      if (totalRemaining > 0) {
        try {
          const permission = await checkContactPermission(targetUserId);
          setCanSeeContact(permission.can_view || false);
        } catch (err) {
          setCanSeeContact(err?.response?.data?.already_viewed ? true : false);
        }
      } else {
        // No remaining views - check if user had contact plans (exhausted)
        if (contactPlans.length > 0) {
          setContactLimitReached(true);
        }
        setCanSeeContact(false);
      }

      setContactCheckDone(true);
    } catch (err) {
      console.error("Contact check error:", err);
      setCanSeeContact(false);
      setContactCheckDone(true);
    }
  };

  // ============================================
  // HANDLERS - same as before
  // ============================================
  const handleSendInterest = async () => {
    if (!user?.id) {
      message.error("Invalid user");
      return;
    }

    const targetId = user.id;

    // ✅ Already sent check
    if (interestSentIds.includes(targetId)) {
      message.info("💖 Already sent");
      return;
    }

    // ✅ MAIN CHECK - Profile unlock hai ya nahi
    if (!isCurrentUser && !canSeeContact) {
      message.warning({
        content: (
          <div style={{ lineHeight: 1.6 }}>
            <strong>🔒 Profile Locked!</strong><br />
            <span style={{ fontSize: 12, color: '#888' }}>
              Please unlock this profile first to send interest.
            </span>
          </div>
        ),
        duration: 4,
      });
      return; // ← STOP
    }

    // ✅ Unlocked hai - interest bhejo
    setActionLoading(prev => ({ ...prev, [targetId]: 'interest' }));
    try {
      const response = await sendInterest(targetId);
      if (response && response.id) {
        setInterestSentIds(prev => [...prev, targetId]);
        message.success("💖 Interest sent!");
      }
    } catch (error) {
      message.error("Failed to send interest");
    } finally {
      setActionLoading(prev => ({ ...prev, [targetId]: null }));
    }
  };

  const handleShortlist = async () => {
    if (!user?.id) { message.error("Invalid user"); return; }
    const targetId = user.id;
    if (shortlistedIds.includes(targetId)) { message.info("⭐ Already shortlisted"); return; }
    setActionLoading(prev => ({ ...prev, [targetId]: 'shortlist' }));
    const success = await handleShortlistUser(targetId, setShortlistedIds);
    setActionLoading(prev => ({ ...prev, [targetId]: null }));
    return success;
  };



  const handleShare = () => {
    const profileUrl = `${window.location.origin}/user-profile/${user?.id || userId}`;
    if (navigator.share) {
      navigator.share({ title: `${getDisplayName()}'s Profile`, url: profileUrl })
        .catch(() => copyToClipboard(profileUrl));
    } else {
      copyToClipboard(profileUrl);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
        .then(() => message.success("🔗 Link copied!"))
        .catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); message.success("🔗 Link copied!"); } catch { }
    document.body.removeChild(ta);
  };

  // ============================================
  // ✅ EDIT PROFILE HANDLERS
  // ============================================
  const openEditModal = () => {
    console.log("📝 Opening Edit Modal");
    // Populate form with current user data
    editForm.setFieldsValue({
      firstname: user?.firstname || '',
      gender: user?.gender || '',
      dob: user?.dob || '',
      height: user?.height || '',
      weight: user?.weight || '',
      nationality: user?.nationality || '',
      religion: user?.religion || '',
      caste: user?.caste || '',
      subcaste: user?.subcaste || '',
      mother_tongue: user?.mother_tongue || '',
      marrige_status: user?.marrige_status || '',
      blood_group: user?.blood_group || '',
      state: user?.state || '',
      city: user?.city || '',
      district: user?.district || '',
      country: user?.country || 'India',
      email: user?.email || '',
      contact: user?.contact || '',
      contact1: user?.contact1 || '',
      smoking: user?.smoking || '',
      drinking: user?.drinking || '',
      bio: user?.bio || '',
      father_name: user?.father_name || '',
      mother_name: user?.mother_name || '',
      father_occupation: user?.father_occupation || '',
      mother_occupation: user?.mother_occupation || '',
      brother: user?.brother || 0,
      sister: user?.sister || 0,
      brother_marrige: user?.brother_marrige || 0,
      sister_marrige: user?.sister_marrige || 0,
      about_family: user?.about_family || '',
      university: user?.university || '',
      course: user?.course || '',
      sub_course: user?.sub_course || '',
      job_title: user?.job_title || '',
      job_type: user?.job_type || '',
      company: user?.company || '',
      annual_income: user?.annual_income || 0,
      Lifestyle: user?.Lifestyle || '',
      height_preference: user?.height_preference || '',
      age_preference: user?.age_preference || '',
      education_preference: user?.education_preference || '',
      religion_preference: user?.religion_preference || '',
      location_preference: user?.location_preference || '',
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    console.log("📤 Submitting Edit:", values);
    setEditLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_API}/api/user/update-profile/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        const updatedData = await response.json();
        // Update user state
        setUser(prev => ({ ...prev, ...updatedData }));
        // Update cache
        profileCache.set(userId, updatedData);
        message.success("✅ Profile updated successfully!");
        setEditModalVisible(false);
        // Refresh page data
        window.location.reload();
      } else {
        const errorData = await response.json();
        message.error(errorData.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Update error:", error);
      message.error("An error occurred while updating profile");
    } finally {
      setEditLoading(false);
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const getDisplayName = () =>
    user?.firstname || user?.first_name || user?.username || user?.name || 'User';

  const getDisplayImage = () => {
    if (!user) return `https://ui-avatars.com/api/?name=User&background=ff0055&color=fff&size=200`;
    if (user.image && typeof user.image === 'string' && user.image.startsWith('http')) return user.image;
    if (user.pic && typeof user.pic === 'string') {
      if (user.pic.startsWith('http')) return user.pic;
      if (user.pic.startsWith('/')) return `${BASE_API}${user.pic}`;
      return `${BASE_API}/${user.pic}`;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(getDisplayName())}&background=ff0055&color=fff&size=200`;
  };

  const getDisplayLocation = () => {
    if (!user) return 'N/A';
    if (user.city && user.state) return `${user.city}, ${user.state}`;
    return user.city || user.district || user.state || 'N/A';
  };

  const getDisplayEducation = () => user?.course || user?.education || 'N/A';
  const getDisplayJob = () => user?.job_title || user?.occupation || 'N/A';

  const getRemainingViews = () => {
    const contactPlans = activePlans.filter(p => p.plan_type === 'contact_view');
    if (contactPlans.length === 0) return 0;

    const isUnlimited = contactPlans.some(p => p.contact_view_limit >= 9999);
    if (isUnlimited) return '∞';

    return contactPlans.reduce((sum, p) => sum + (p.remaining_views || 0), 0);
  };

  const getContactPlansCount = () => {
    return activePlans.filter(p => p.plan_type === 'contact_view').length;
  };

  // ⭐ NEW: Check if user has any active plan
  const hasAnyActivePlan = () => activePlans.length > 0;

  // ⭐ NEW: Get boost plans count
  const getBoostPlansCount = () => {
    return activePlans.filter(p => p.plan_type === 'profile_boost').length;
  };

  // ============================================
  // RENDER HELPERS
  // ============================================
  const renderDetailItem = (label, value, icon) => {
    if (value === null || value === undefined || value === 'N/A' || value === '' || value === ' ') return null;

    let displayValue = value;

    if (label === 'Date of Birth' && value) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          displayValue = date.toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric'
          });
        }
      } catch (e) { }
    }

    if (label === 'Annual Income' && value) {
      displayValue = `₹${Number(value).toLocaleString('en-IN')}`;
    }

    if (value === 0) displayValue = '0';

    return (
      <div className="up-detail-item">
        <p className="up-detail-label">
          {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
          {label}
        </p>
        <p className="up-detail-value">{displayValue}</p>
      </div>
    );
  };

  const renderContactItem = (label, value, icon) => {
    if (!value) return null;
    if (isCurrentUser || canSeeContact) return renderDetailItem(label, value, icon);

    return (
      <div className="up-detail-item" style={{
        background: 'linear-gradient(135deg, #f8f8f8, #f0f0f0)',
        border: '1.5px solid #e0e0e0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(255,255,255,0.3)',
          backdropFilter: 'blur(2px)',
          zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <FaLock size={14} style={{ color: '#999' }} />
        </div>
        <p className="up-detail-label" style={{ color: '#bbb', position: 'relative', zIndex: 0 }}>
          {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
          {label}
        </p>
        <p style={{
          fontSize: 14, fontWeight: 700, color: '#ccc',
          margin: 0, letterSpacing: 3, filter: 'blur(4px)', userSelect: 'none'
        }}>
          {label === 'Email' ? '••••••@•••.com'
            : label.includes('Number') || label === 'Mobile' ? '•••••-•••••'
              : label === 'City' || label === 'District' ? '•••••••'
                : '••••••••'}
        </p>
      </div>
    );
  };

  // ============================================
  // LOADING
  // ============================================
  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '60vh', flexDirection: 'column', gap: 16
      }}>
        <FaSpinner size={48} style={{ color: '#e91e63', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#6b7280', fontSize: 16 }}>Loading Profile...</p>
        <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '60vh', padding: 20
      }}>
        <div style={{
          background: '#fff', borderRadius: 20, padding: 40,
          maxWidth: 500, textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
        }}>
          <h2 style={{ color: '#e91e63', marginBottom: 12 }}>⚠️ User Not Found</h2>
          <p style={{ color: '#6b7280' }}>{error || 'Profile does not exist.'}</p>
          <button
            onClick={() => navigate('/all-users')}
            style={{
              marginTop: 20, padding: '10px 30px',
              background: 'linear-gradient(135deg, #e91e63, #ff4d88)',
              color: '#fff', border: 'none', borderRadius: 50,
              fontSize: 14, fontWeight: 600, cursor: 'pointer'
            }}
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
        * { font-family: 'Poppins', sans-serif !important; box-sizing: border-box; margin: 0; }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes upShimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }

        .up-page { background: #fff; min-height: 100vh; padding: 20px 16px; }
        .up-wrap { max-width: 1280px; margin: 0 auto; }
        .up-back-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; background: transparent; border: 2px solid #ffe4ec; border-radius: 12px; cursor: pointer; font-weight: 600; color: #555; transition: all .3s; margin-bottom: 20px; }
        .up-back-btn:hover { border-color: #ff0055; color: #ff0055; background: #fff0f3; }
        .up-header-card { background: #fff; border: 2px solid #ffe4ec; border-radius: 24px; padding: 28px; margin-bottom: 18px; box-shadow: 0 10px 30px rgba(255,0,85,.06); display: flex; gap: 28px; flex-wrap: wrap; position: relative; overflow: hidden; }
        .up-header-card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 6px; background: linear-gradient(90deg, #ff0055, #ff4d88, #ff80aa, #ff0055); background-size: 200% 100%; animation: upShimmer 3s linear infinite; }
        .up-avatar-section { display: flex; flex-direction: column; align-items: center; gap: 14px; flex-shrink: 0; }
        .up-avatar-wrap { position: relative; padding: 5px; background: linear-gradient(135deg, #ff0055, #ff4d88, #ff80aa); border-radius: 50%; box-shadow: 0 12px 30px rgba(255,0,85,.22); }
        .up-avatar { width: 140px; height: 140px; border-radius: 50%; object-fit: cover; border: 4px solid #fff; }
        .up-online-dot { position: absolute; bottom: 8px; right: 12px; width: 22px; height: 22px; border-radius: 50%; background: #4caf50; border: 3px solid #fff; }
        .up-badge-row { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
        .up-badge { display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; border-radius: 999px; font-size: 11.5px; font-weight: 700; }
        .up-badge.verified { background: linear-gradient(135deg, #e8f5e9, #c8e6c9); color: #2e7d32; border: 1px solid #a5d6a7; }
        .up-badge.premium { background: linear-gradient(135deg, #fff3e0, #ffe0b2); color: #e65100; border: 1px solid #ffcc80; }
        .up-info { flex: 1; min-width: 220px; }
        .up-name { font-size: 28px; font-weight: 800; color: #1a0a10; margin: 0; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .up-name-gender { font-size: 14px; font-weight: 500; color: #888; }
        .up-bid { font-size: 13px; color: #ff4d88; font-weight: 700; margin: 6px 0; padding: 4px 14px; border-radius: 999px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); display: inline-block; border: 1px solid #ffd3df; }
        .up-quick-info { font-size: 14px; color: #555; margin: 8px 0; font-weight: 500; }
        .up-tags { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0; }
        .up-tag { display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); border: 1px solid #ffd3df; border-radius: 999px; font-size: 11.5px; color: #c2185b; font-weight: 600; }
        .up-tag.online { background: linear-gradient(135deg, #e8f5e9, #c8e6c9); color: #4caf50; border-color: #a5d6a7; }
        .up-actions { display: flex; gap: 8px; margin-top: 14px; flex-wrap: wrap; }
        .up-action-btn { padding: 10px 18px; border: none; border-radius: 12px; font-size: 13px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all .25s; }
        .up-action-btn:hover { transform: translateY(-2px); }
        .up-action-btn:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
        .up-btn-heart { background: linear-gradient(135deg, #4caf50, #66bb6a); color: #fff; }
        .up-btn-star { background: linear-gradient(135deg, #ff9800, #ffb74d); color: #fff; }
        .up-btn-share { background: #fff; color: #ff0055; border: 1.5px solid #ff0055 !important; }
        .up-btn-hide { background: linear-gradient(135deg, #fff0f3, #ffe4ec); color: #c2185b; border: 1.5px solid #ffd3df !important; }
        .up-btn-edit { background: linear-gradient(135deg, #2196f3, #64b5f6); color: #fff; border: none !important; }
        .up-btn-edit:hover { background: linear-gradient(135deg, #1976d2, #2196f3); }
        .up-contact-banner { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border-radius: 16px; margin-bottom: 18px; }
        .up-tabs { display: flex; gap: 4px; padding: 5px; background: #fff0f3; border: 2px solid #ffe4ec; border-radius: 14px; margin-bottom: 18px; overflow-x: auto; }
        .up-tab { padding: 10px 18px; border: none; border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 700; transition: all .25s; background: transparent; color: #888; display: flex; align-items: center; gap: 6px; white-space: nowrap; }
        .up-tab:hover { color: #ff0055; }
        .up-tab.active { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; box-shadow: 0 4px 14px rgba(255,0,85,.2); }
        .up-content { background: #fff; border: 2px solid #ffe4ec; border-radius: 20px; padding: 26px; margin-bottom: 22px; }
        .up-section-title { font-size: 19px; font-weight: 800; color: #1a0a10; margin: 0 0 18px; display: flex; align-items: center; gap: 10px; padding-bottom: 14px; border-bottom: 2px solid #fff0f3; }
        .up-section-title svg { color: #ff0055; }
        .up-details-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
        .up-detail-item { display: flex; flex-direction: column; gap: 4px; padding: 14px; background: linear-gradient(135deg, #fffafc, #fff0f3); border: 1.5px solid #ffe4ec; border-radius: 12px; transition: all .25s; }
        .up-detail-item:hover { border-color: #ffb3c8; box-shadow: 0 4px 12px rgba(255,0,85,.05); }
        .up-detail-label { font-size: 11px; color: #c2185b; margin: 0; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; }
        .up-detail-value { font-size: 14px; color: #1a0a10; font-weight: 600; margin: 0; }
        .up-about-section { margin-top: 22px; padding: 18px 20px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); border: 1.5px solid #ffd3df; border-radius: 14px; }
        .up-about-title { font-size: 16px; font-weight: 800; color: #c2185b; margin: 0 0 10px; display: flex; align-items: center; gap: 8px; }
        .up-about-text { font-size: 13.5px; color: #555; line-height: 1.7; margin: 0; }
        .up-hobbies-section { margin-top: 22px; }
        .up-hobbies-title { font-size: 16px; font-weight: 800; color: #1a0a10; margin: 0 0 12px; display: flex; align-items: center; gap: 8px; }
        .up-hobbies-list { display: flex; flex-wrap: wrap; gap: 8px; }
        .up-hobby-tag { padding: 7px 16px; background: linear-gradient(135deg, #fff0f3, #ffe4ec); border: 1.5px solid #ffd3df; border-radius: 999px; font-size: 12.5px; color: #c2185b; font-weight: 600; transition: all .25s; }
        .up-hobby-tag:hover { background: linear-gradient(135deg, #ff0055, #ff4d88); color: #fff; border-color: #ff0055; }
        .up-hobby-tag.interest { background: linear-gradient(135deg, #e8f5e9, #c8e6c9); border-color: #a5d6a7; color: #2e7d32; }

        @media (max-width: 1024px) { .up-details-grid { grid-template-columns: 1fr; } }
        @media (max-width: 768px) {
          .up-header-card { flex-direction: column; align-items: center; text-align: center; }
          .up-actions { justify-content: center; }
          .up-name { justify-content: center; }
          .up-tabs { flex-wrap: wrap; }
          .up-tab { flex: 1; justify-content: center; }
        }
        @media (max-width: 480px) {
          .up-name { font-size: 22px; }
          .up-avatar { width: 110px; height: 110px; }
          .up-content { padding: 20px 16px; }
          .up-page { padding: 16px 12px; }
        }
      `}</style>

      <div className="up-page">
        <div className="up-wrap">

          <button className="up-back-btn" onClick={() => navigate('/all-users')}>
            <FaArrowLeft size={16} /> Back to Users
          </button>

          {/* PROFILE HEADER */}
          <div className="up-header-card">
            <div className="up-avatar-section">
              <div className="up-avatar-wrap">
                <img src={getDisplayImage()} alt={getDisplayName()} className="up-avatar" />
                {user?.is_online && <span className="up-online-dot" />}
              </div>
              <div className="up-badge-row">
                {user?.verified && <span className="up-badge verified"><FaCheckCircle size={11} /> Verified</span>}
                {user?.premium && <span className="up-badge premium"><FaStar size={11} /> Premium</span>}
              </div>
            </div>

            <div className="up-info">
              <h1 className="up-name">
                {getDisplayName()}
                {user?.gender && (
                  <span className="up-name-gender">
                    ({user.gender === 'Male' ? '♂️' : '♀️'} {user.gender})
                  </span>
                )}
              </h1>
              <div className="up-bid">Profile ID: {user?.profile_id || 'N/A'}</div>
              <p className="up-quick-info">
                🎂 {user?.age || 0} Yrs &nbsp;•&nbsp;
                📏 {user?.height || 'N/A'} &nbsp;•&nbsp;
                📍 {getDisplayLocation()}
              </p>

              <div className="up-tags">
                <span className={`up-tag ${user?.is_online ? 'online' : ''}`}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: user?.is_online ? '#4caf50' : '#bbb' }} />
                  {user?.is_online ? 'Online' : 'Offline'}
                </span>
                {user?.marrige_status && <span className="up-tag"><FaRing size={10} /> {user.marrige_status}</span>}
                {user?.religion && <span className="up-tag"><FaPray size={10} /> {user.religion}</span>}
                {user?.caste && <span className="up-tag"><FaUsers size={10} /> {user.caste}</span>}
                {user?.mother_tongue && <span className="up-tag"><FaLanguage size={10} /> {user.mother_tongue}</span>}
              </div>

              <div className="up-actions">
                {isCurrentUser && (
                  <button className="up-action-btn up-btn-edit" onClick={openEditModal}>
                    <FaEdit size={12} /> Edit Profile
                  </button>
                )}
                {!isCurrentUser && !interestSentIds.includes(user?.id) && (
                  <button
                    className="up-action-btn"
                    onClick={handleSendInterest}
                    disabled={actionLoading[user?.id] === 'interest'}
                    title={
                      !canSeeContact
                        ? "🔒 Unlock profile first to send interest"
                        : "Send Interest"
                    }
                    style={{
                      // ✅ Lock hai to gray, unlocked to green
                      background: !canSeeContact
                        ? 'linear-gradient(135deg, #9e9e9e, #bdbdbd)'
                        : 'linear-gradient(135deg, #4caf50, #66bb6a)',
                      color: '#fff',
                      border: 'none',
                    }}
                  >
                    {actionLoading[user?.id] === 'interest' ? (
                      <FaSpinner size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : !canSeeContact ? (
                      <><FaLock size={12} /> Unlock First</>
                    ) : (
                      <><FaHeart size={12} /> Send Interest</>
                    )}
                  </button>
                )}
                {!isCurrentUser && interestSentIds.includes(user?.id) && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)', color: '#2e7d32', border: '1.5px solid #a5d6a7', borderRadius: 12, fontSize: 13, fontWeight: 700 }}>
                    <FaCheck size={12} /> Interest Sent ✓
                  </span>
                )}
                {!isCurrentUser && !shortlistedIds.includes(user?.id) && (
                  <button className="up-action-btn up-btn-star" onClick={handleShortlist} disabled={actionLoading[user?.id] === 'shortlist'}>
                    {actionLoading[user?.id] === 'shortlist'
                      ? <FaSpinner size={12} style={{ animation: 'spin 1s linear infinite' }} />
                      : <><FaStar size={12} /> Shortlist</>}
                  </button>
                )}
                {!isCurrentUser && shortlistedIds.includes(user?.id) && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: 'linear-gradient(135deg, #fff3e0, #ffe0b2)', color: '#e65100', border: '1.5px solid #ffcc80', borderRadius: 12, fontSize: 13, fontWeight: 700 }}>
                    <FaStar size={12} /> Shortlisted ⭐
                  </span>
                )}
                <button className="up-action-btn up-btn-share" onClick={handleShare}>
                  <FaShare size={12} /> Share
                </button>

              </div>
            </div>
          </div>

          {/* CONTACT BANNER - MULTIPLE PLANS SUPPORT */}
          {!isCurrentUser && contactCheckDone && (
            <>
              {/* Show total plans indicator if multiple */}
              {activePlans.length > 1 && canSeeContact && (
                <div style={{
                  background: 'linear-gradient(135deg, #fff0f3, #ffe4ec)',
                  border: '2px solid #ffd3df',
                  borderRadius: 14,
                  padding: '10px 14px',
                  marginBottom: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 8,
                }}>
                  <span style={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: '#c2185b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                    💎 You have {activePlans.length} active plans
                  </span>
                  <button
                    onClick={() => navigate('/recharge')}
                    style={{
                      padding: '6px 14px',
                      background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 999,
                      fontSize: 10.5,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Manage Plans
                  </button>
                </div>
              )}

              {/* Contact Banner */}
              <div className="up-contact-banner" style={{
                background: canSeeContact
                  ? 'linear-gradient(135deg, #eff6ff, #dbeafe)'
                  : 'linear-gradient(135deg, #fff0f3, #ffe4ec)',
                border: canSeeContact ? '2px solid #bfdbfe' : '2px solid #ffd3df',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                  background: canSeeContact
                    ? 'linear-gradient(135deg, #3b82f6, #60a5fa)'
                    : 'linear-gradient(135deg, #ff0055, #ff4d88)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                }}>
                  {canSeeContact ? '📱' : '🔒'}
                </div>

                <div style={{ flex: 1 }}>
                  {canSeeContact ? (
                    <>
                      <p style={{ fontSize: 13, fontWeight: 800, color: '#1e40af', margin: 0 }}>
                        Contact Details Unlocked
                        {getContactPlansCount() > 1 && (
                          <span style={{
                            marginLeft: 8, fontSize: 10, background: '#3b82f6',
                            color: '#fff', padding: '2px 8px', borderRadius: 999,
                            fontWeight: 700
                          }}>
                            {getContactPlansCount()} PLANS
                          </span>
                        )}
                      </p>
                      <p style={{ fontSize: 11, color: '#3b82f6', margin: '2px 0 0', fontWeight: 600 }}>
                        {getRemainingViews() === '∞'
                          ? 'Unlimited contact views remaining'
                          : `${getRemainingViews()} contact views remaining ${getContactPlansCount() > 1 ? '(from all plans)' : ''}`
                        }
                      </p>
                    </>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, fontWeight: 800, color: '#c2185b', margin: 0 }}>
                        {contactLimitReached ? '❌ Contact View Limit Reached' : '🔒 Contact Details are Hidden'}
                      </p>
                      <p style={{ fontSize: 11, color: '#e91e63', margin: '2px 0 0', fontWeight: 500 }}>
                        {contactLimitReached
                          ? 'All your contact views are used. Upgrade to view more!'
                          : 'Purchase a Contact View Plan to see details'}
                      </p>
                    </>
                  )}
                </div>

                {canSeeContact ? (
                  <div style={{
                    textAlign: 'center', padding: '4px 14px',
                    background: '#fff', borderRadius: 12,
                    border: '1.5px solid #bfdbfe'
                  }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: '#3b82f6', lineHeight: 1 }}>
                      {getRemainingViews()}
                    </div>
                    <div style={{ fontSize: 9, color: '#93c5fd', fontWeight: 700, marginTop: 2 }}>
                      {getRemainingViews() === '∞' ? 'UNLIMITED' : 'LEFT'}
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => navigate('/recharge')}
                    style={{
                      padding: '9px 18px', flexShrink: 0,
                      background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                      color: '#fff', border: 'none', borderRadius: 999,
                      fontSize: 12, fontWeight: 700, cursor: 'pointer'
                    }}
                  >
                    {contactLimitReached ? '🔄 Upgrade' : 'View Plans'}
                  </button>
                )}
              </div>

              {/* ⭐ NEW: Boost Plans Banner (if user has boost) */}
              {getBoostPlansCount() > 0 && (
                <div className="up-contact-banner" style={{
                  background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                  border: '2px solid #fde68a',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                    background: 'linear-gradient(135deg, #ffd700, #ffb300)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                  }}>
                    🚀
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, fontWeight: 800, color: '#92400e', margin: 0 }}>
                      Profile Boost Active!
                      {getBoostPlansCount() > 1 && (
                        <span style={{
                          marginLeft: 8, fontSize: 10, background: '#ffb300',
                          color: '#fff', padding: '2px 8px', borderRadius: 999,
                          fontWeight: 700
                        }}>
                          {getBoostPlansCount()} BOOSTS
                        </span>
                      )}
                    </p>
                    <p style={{ fontSize: 11, color: '#b45309', margin: '2px 0 0', fontWeight: 500 }}>
                      Your profile is showing at top position on all dashboards
                    </p>
                  </div>
                  <span style={{
                    padding: '4px 10px', flexShrink: 0,
                    background: 'linear-gradient(135deg, #ffd700, #ffb300)',
                    color: '#1a0a10', borderRadius: 999,
                    fontSize: 9, fontWeight: 800, whiteSpace: 'nowrap'
                  }}>
                    ⭐ BOOSTED
                  </span>
                </div>
              )}
            </>
          )}

          {/* TABS */}
          <div className="up-tabs">
            {[
              { key: 'details', label: 'Personal', icon: <FaUser size={12} /> },
              { key: 'family', label: 'Family', icon: <FaUsers size={12} /> },
              { key: 'education', label: 'Education', icon: <FaGraduationCap size={12} /> },
              { key: 'preferences', label: 'Preferences', icon: <FaHeart size={12} /> },
            ].map(tab => (
              <button
                key={tab.key}
                className={`up-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* CONTENT */}
          <div className="up-content">

            {activeTab === 'details' && (
              <>
                <h3 className="up-section-title"><FaUser /> Personal Details</h3>
                <div className="up-details-grid">
                  {renderDetailItem('Full Name', getDisplayName(), '👤')}
                  {renderDetailItem('Profile ID', user?.profile_id, '🆔')}
                  {renderDetailItem('Gender', user?.gender, '⚧')}
                  {renderDetailItem('Age', user?.age ? `${user.age} Years` : null, '🎂')}
                  {renderDetailItem('Date of Birth', user?.dob, '📅')}
                  {renderDetailItem('Height', user?.height, '📏')}
                  {renderDetailItem('Weight', user?.weight, '⚖️')}
                  {renderDetailItem('Nationality', user?.nationality, '🌍')}
                  {renderDetailItem('Religion', user?.religion, '🕉️')}
                  {renderDetailItem('Caste', user?.caste, '🙏')}
                  {renderDetailItem('Sub Caste', user?.subcaste, '🙏')}
                  {renderDetailItem('Mother Tongue', user?.mother_tongue, '🗣️')}
                  {renderDetailItem('Languages Known', user?.languages?.join(', '), '💬')}
                  {renderDetailItem('Marital Status', user?.marrige_status, '💍')}
                  {renderDetailItem('Blood Group', user?.blood_group, '🩸')}
                  {renderDetailItem('State', user?.state, '🗺️')}
                  {renderDetailItem('Country', user?.country || 'India', '🌏')}
                  {renderDetailItem('Smoking', user?.smoking, '🚬')}
                  {renderDetailItem('Drinking', user?.drinking, '🍷')}
                  {renderContactItem('Mobile Number', user?.contact, '📱')}
                  {renderContactItem('Alternate Number', user?.contact1, '📱')}
                  {renderContactItem('Email', user?.email, '📧')}
                  {renderContactItem('City', user?.city, '🏙️')}
                  {renderContactItem('District', user?.district, '🏙️')}
                </div>

                {!isCurrentUser && !canSeeContact && (
                  <div style={{
                    marginTop: 16, padding: '14px 18px',
                    background: 'linear-gradient(135deg, #fff0f3, #ffe4ec)',
                    border: '1.5px solid #ffd3df', borderRadius: 14,
                    display: 'flex', alignItems: 'center', gap: 12
                  }}>
                    <span style={{ fontSize: 24 }}>🔒</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: '#c2185b', margin: 0 }}>
                        {contactLimitReached ? 'All contact views used' : 'Contact details are locked'}
                      </p>
                      <p style={{ fontSize: 11, color: '#e91e63', margin: '3px 0 0' }}>
                        {contactLimitReached
                          ? `Upgrade your plan to view more contacts.`
                          : `Purchase a Contact View Plan to unlock. ${activePlans.length > 0 ? `(You have ${activePlans.length} other active plans)` : ''}`
                        }
                      </p>
                    </div>
                    <button
                      onClick={() => navigate('/recharge')}
                      style={{
                        padding: '8px 16px', flexShrink: 0,
                        background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                        color: '#fff', border: 'none', borderRadius: 999,
                        fontSize: 12, fontWeight: 700, cursor: 'pointer'
                      }}
                    >
                      {contactLimitReached ? 'Upgrade' : 'Get Plan'}
                    </button>
                  </div>
                )}

                {user?.additional_notes && (
                  <div className="up-about-section">
                    <h4 className="up-about-title"><FaNotesMedical /> Additional Notes</h4>
                    <p className="up-about-text">{user.additional_notes}</p>
                  </div>
                )}
                {user?.bio && (
                  <div className="up-about-section">
                    <h4 className="up-about-title">💖 About Me</h4>
                    <p className="up-about-text">{user.bio}</p>
                  </div>
                )}
                {(user?.hobbies?.length > 0 || user?.interests?.length > 0) && (
                  <div className="up-hobbies-section">
                    <h4 className="up-hobbies-title"><FaStar /> Hobbies & Interests</h4>
                    <div className="up-hobbies-list">
                      {user?.hobbies?.map((h, i) => <span key={i} className="up-hobby-tag">{h}</span>)}
                      {user?.interests?.map((item, idx) => <span key={idx} className="up-hobby-tag interest">{item}</span>)}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'family' && (
              <>
                <h3 className="up-section-title"><FaUsers /> Family Background</h3>
                <div className="up-details-grid">
                  {renderDetailItem("Father's Name", user?.father_name, '👨')}
                  {renderDetailItem("Father's Occupation", user?.father_occupation, '💼')}
                  {renderDetailItem("Mother's Name", user?.mother_name, '👩')}
                  {renderDetailItem("Mother's Occupation", user?.mother_occupation, '💼')}
                  {renderDetailItem('Total Brothers', user?.brother, '👦')}
                  {renderDetailItem('Married Brothers', user?.brother_marrige, '💒')}
                  {renderDetailItem('Total Sisters', user?.sister, '👧')}
                  {renderDetailItem('Married Sisters', user?.sister_marrige, '💒')}
                </div>
                {user?.about_family && (
                  <div className="up-about-section">
                    <h4 className="up-about-title">👨‍👩‍👧 About Family</h4>
                    <p className="up-about-text">{user.about_family}</p>
                  </div>
                )}
              </>
            )}

            {activeTab === 'education' && (
              <>
                <h3 className="up-section-title"><FaGraduationCap /> Education & Career</h3>
                <div className="up-details-grid">
                  {renderDetailItem('University', user?.university, '🏛️')}
                  {renderDetailItem('Course / Degree', getDisplayEducation(), '📖')}
                  {renderDetailItem('Sub Course', user?.sub_course, '📚')}
                  {renderDetailItem('Job Profession', getDisplayJob(), '💼')}
                  {renderDetailItem('Job Title', user?.job_title, '💼')}
                  {renderDetailItem('Employment Type', user?.job_type, '🏢')}
                  {renderDetailItem('Company', user?.company, '🏢')}
                  {renderDetailItem('Annual Income', user?.annual_income ? `₹${user.annual_income.toLocaleString()}` : null, '💰')}
                  {renderDetailItem('Lifestyle', user?.Lifestyle, '🌟')}
                  {renderDetailItem('Profile Managed By', user?.created_profile, '👤')}
                </div>
              </>
            )}

            {activeTab === 'preferences' && (
              <>
                <h3 className="up-section-title"><FaHeart /> Partner Preferences</h3>
                <div className="up-details-grid">
                  {renderDetailItem('Height Preference', user?.height_preference, '📏')}
                  {renderDetailItem('Age Preference', user?.age_preference, '🎂')}
                  {renderDetailItem('Education Preference', user?.education_preference, '📚')}
                  {renderDetailItem('Religion Preference', user?.religion_preference, '🕉️')}
                  {renderDetailItem('Location Preference', user?.location_preference, '📍')}
                </div>
              </>
            )}

          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* ✅ EDIT PROFILE MODAL - Inline Styling Approach */}
      {/* ============================================ */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <EditOutlined style={{ color: '#ff0055', fontSize: 22 }} />
            <span style={{ fontSize: 20, fontWeight: 800, color: '#1a0a10' }}>Edit Profile</span>
          </div>
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={900}
        destroyOnClose
        maskClosable={false}
        bodyStyle={{ maxHeight: '70vh', overflow: 'auto', padding: '24px' }}
        style={{ top: 20 }}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
          requiredMark="optional"
          scrollToFirstError
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
            {/* Personal Details Section */}
            <div style={{ gridColumn: '1 / -1' }}>
              <Divider orientation="left">
                <Space>
                  <FaUser style={{ color: '#ff0055' }} />
                  <span style={{ fontWeight: 700, color: '#1a0a10' }}>Personal Details</span>
                </Space>
              </Divider>
            </div>

            <Form.Item
              name="firstname"
              label="Full Name"
              rules={[{ required: true, message: 'Please enter your name' }]}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter your full name"
                prefix={<FaUser size={14} style={{ color: '#999' }} />}
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="gender"
              label="Gender"
              rules={[{ required: true, message: 'Please select gender' }]}
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select gender"
                style={{ borderRadius: 10, height: 42 }}
                suffixIcon={<FaTransgender size={14} />}
              >
                <Option value="Male">♂️ Male</Option>
                <Option value="Female">♀️ Female</Option>
                <Option value="Other">⚧ Other</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="dob"
              label="Date of Birth"
              rules={[{ required: true, message: 'Please enter date of birth' }]}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="YYYY-MM-DD"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="height"
              label="Height"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="e.g., 5'8 or 172cm"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="weight"
              label="Weight (kg)"
              style={{ marginBottom: 16 }}
            >
              <InputNumber
                style={{ width: '100%', borderRadius: 10, height: 42 }}
                placeholder="Enter weight"
                min={30}
                max={300}
              />
            </Form.Item>

            <Form.Item
              name="nationality"
              label="Nationality"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter nationality"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="religion"
              label="Religion"
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select religion"
                style={{ borderRadius: 10, height: 42 }}
              >
                <Option value="Hindu">🕉️ Hindu</Option>
                <Option value="Muslim">☪️ Muslim</Option>
                <Option value="Christian">✝️ Christian</Option>
                <Option value="Sikh">🪯 Sikh</Option>
                <Option value="Buddhist">☸️ Buddhist</Option>
                <Option value="Jain">✡️ Jain</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="caste"
              label="Caste"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter caste"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="subcaste"
              label="Sub Caste"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter sub caste"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="mother_tongue"
              label="Mother Tongue"
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select mother tongue"
                style={{ borderRadius: 10, height: 42 }}
              >
                <Option value="Hindi">🇮🇳 Hindi</Option>
                <Option value="English">🇬🇧 English</Option>
                <Option value="Tamil">🏝️ Tamil</Option>
                <Option value="Telugu">🏝️ Telugu</Option>
                <Option value="Kannada">🏝️ Kannada</Option>
                <Option value="Malayalam">🏝️ Malayalam</Option>
                <Option value="Marathi">🏝️ Marathi</Option>
                <Option value="Gujarati">🏝️ Gujarati</Option>
                <Option value="Bengali">🏝️ Bengali</Option>
                <Option value="Punjabi">🏝️ Punjabi</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="marrige_status"
              label="Marital Status"
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select marital status"
                style={{ borderRadius: 10, height: 42 }}
              >
                <Option value="Unmarried">💍 Unmarried</Option>
                <Option value="Married">💒 Married</Option>
                <Option value="Divorced">💔 Divorced</Option>
                <Option value="Widowed">🕊️ Widowed</Option>
                <Option value="Separated">↔️ Separated</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="blood_group"
              label="Blood Group"
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select blood group"
                style={{ borderRadius: 10, height: 42 }}
              >
                <Option value="A+">🅰️ A+</Option>
                <Option value="A-">🅰️ A-</Option>
                <Option value="B+">🅱️ B+</Option>
                <Option value="B-">🅱️ B-</Option>
                <Option value="AB+">🆎 AB+</Option>
                <Option value="AB-">🆎 AB-</Option>
                <Option value="O+">🅾️ O+</Option>
                <Option value="O-">🅾️ O-</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="state"
              label="State"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter state"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="city"
              label="City"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter city"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="district"
              label="District"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter district"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="country"
              label="Country"
              initialValue="India"
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select country"
                style={{ borderRadius: 10, height: 42 }}
              >
                <Option value="India">🇮🇳 India</Option>
                <Option value="USA">🇺🇸 USA</Option>
                <Option value="UK">🇬🇧 UK</Option>
                <Option value="Canada">🇨🇦 Canada</Option>
                <Option value="Australia">🇦🇺 Australia</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="smoking"
              label="Smoking"
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select smoking habit"
                style={{ borderRadius: 10, height: 42 }}
              >
                <Option value="Never">🚭 Never</Option>
                <Option value="Occasionally">🚬 Occasionally</Option>
                <Option value="Regularly">🚬 Regularly</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="drinking"
              label="Drinking"
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select drinking habit"
                style={{ borderRadius: 10, height: 42 }}
              >
                <Option value="Never">🍷 Never</Option>
                <Option value="Occasionally">🍷 Occasionally</Option>
                <Option value="Regularly">🍷 Regularly</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="contact"
              label="Mobile Number"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter mobile number"
                prefix={<FaPhone size={14} style={{ color: '#999' }} />}
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="contact1"
              label="Alternate Number"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter alternate number"
                prefix={<FaPhone size={14} style={{ color: '#999' }} />}
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: 'email', message: 'Please enter a valid email' }]}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter email"
                prefix={<FaEnvelope size={14} style={{ color: '#999' }} />}
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="bio"
              label="About Me"
              style={{ gridColumn: '1 / -1', marginBottom: 16 }}
            >
              <TextArea
                rows={3}
                placeholder="Tell others about yourself..."
                maxLength={500}
                showCount
                style={{ borderRadius: 10 }}
              />
            </Form.Item>

            {/* Family Details Section */}
            <div style={{ gridColumn: '1 / -1' }}>
              <Divider orientation="left">
                <Space>
                  <FaUsers style={{ color: '#ff0055' }} />
                  <span style={{ fontWeight: 700, color: '#1a0a10' }}>Family Details</span>
                </Space>
              </Divider>
            </div>

            <Form.Item
              name="father_name"
              label="Father's Name"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter father's name"
                prefix={<FaUser size={14} style={{ color: '#999' }} />}
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="father_occupation"
              label="Father's Occupation"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter father's occupation"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="mother_name"
              label="Mother's Name"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter mother's name"
                prefix={<FaUser size={14} style={{ color: '#999' }} />}
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="mother_occupation"
              label="Mother's Occupation"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter mother's occupation"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="brother"
              label="Total Brothers"
              style={{ marginBottom: 16 }}
            >
              <InputNumber
                style={{ width: '100%', borderRadius: 10, height: 42 }}
                placeholder="0"
                min={0}
                max={20}
              />
            </Form.Item>

            <Form.Item
              name="brother_marrige"
              label="Married Brothers"
              style={{ marginBottom: 16 }}
            >
              <InputNumber
                style={{ width: '100%', borderRadius: 10, height: 42 }}
                placeholder="0"
                min={0}
                max={20}
              />
            </Form.Item>

            <Form.Item
              name="sister"
              label="Total Sisters"
              style={{ marginBottom: 16 }}
            >
              <InputNumber
                style={{ width: '100%', borderRadius: 10, height: 42 }}
                placeholder="0"
                min={0}
                max={20}
              />
            </Form.Item>

            <Form.Item
              name="sister_marrige"
              label="Married Sisters"
              style={{ marginBottom: 16 }}
            >
              <InputNumber
                style={{ width: '100%', borderRadius: 10, height: 42 }}
                placeholder="0"
                min={0}
                max={20}
              />
            </Form.Item>

            <Form.Item
              name="about_family"
              label="About Family"
              style={{ gridColumn: '1 / -1', marginBottom: 16 }}
            >
              <TextArea
                rows={3}
                placeholder="Tell about your family..."
                maxLength={500}
                showCount
                style={{ borderRadius: 10 }}
              />
            </Form.Item>

            {/* Education & Career Section */}
            <div style={{ gridColumn: '1 / -1' }}>
              <Divider orientation="left">
                <Space>
                  <FaGraduationCap style={{ color: '#ff0055' }} />
                  <span style={{ fontWeight: 700, color: '#1a0a10' }}>Education & Career</span>
                </Space>
              </Divider>
            </div>

            <Form.Item
              name="university"
              label="University"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter university name"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="course"
              label="Course / Degree"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter course name"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="sub_course"
              label="Sub Course"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter sub course"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="job_title"
              label="Job Title"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter job title"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="job_type"
              label="Employment Type"
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select employment type"
                style={{ borderRadius: 10, height: 42 }}
              >
                <Option value="Full Time">🏢 Full Time</Option>
                <Option value="Part Time">⏰ Part Time</Option>
                <Option value="Self Employed">💼 Self Employed</Option>
                <Option value="Business Owner">🏪 Business Owner</Option>
                <Option value="Government">🏛️ Government</Option>
                <Option value="Private">🏢 Private</Option>
                <Option value="Not Working">📉 Not Working</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="company"
              label="Company Name"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter company name"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="annual_income"
              label="Annual Income (₹)"
              style={{ marginBottom: 16 }}
            >
              <InputNumber
                style={{ width: '100%', borderRadius: 10, height: 42 }}
                placeholder="Enter annual income"
                min={0}
                step={1000}
                prefix="₹"
              />
            </Form.Item>

            <Form.Item
              name="Lifestyle"
              label="Lifestyle"
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select lifestyle"
                style={{ borderRadius: 10, height: 42 }}
              >
                <Option value="Active">🏃 Active</Option>
                <Option value="Moderate">🚶 Moderate</Option>
                <Option value="Relaxed">🧘 Relaxed</Option>
              </Select>
            </Form.Item>

            {/* Partner Preferences Section */}
            <div style={{ gridColumn: '1 / -1' }}>
              <Divider orientation="left">
                <Space>
                  <FaHeart style={{ color: '#ff0055' }} />
                  <span style={{ fontWeight: 700, color: '#1a0a10' }}>Partner Preferences</span>
                </Space>
              </Divider>
            </div>

            <Form.Item
              name="height_preference"
              label="Height Preference"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="e.g., 5'5 - 5'10"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="age_preference"
              label="Age Preference"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="e.g., 25-35"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="education_preference"
              label="Education Preference"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter education preference"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>

            <Form.Item
              name="religion_preference"
              label="Religion Preference"
              style={{ marginBottom: 16 }}
            >
              <Select
                placeholder="Select religion preference"
                style={{ borderRadius: 10, height: 42 }}
              >
                <Option value="Any">🌍 Any</Option>
                <Option value="Hindu">🕉️ Hindu</Option>
                <Option value="Muslim">☪️ Muslim</Option>
                <Option value="Christian">✝️ Christian</Option>
                <Option value="Sikh">🪯 Sikh</Option>
                <Option value="Buddhist">☸️ Buddhist</Option>
                <Option value="Jain">✡️ Jain</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="location_preference"
              label="Location Preference"
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder="Enter location preference"
                style={{ borderRadius: 10, height: 42 }}
              />
            </Form.Item>
          </div>

          {/* Footer Buttons */}
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 16, borderTop: '2px solid #f0f0f0' }}>
            <Button
              onClick={() => setEditModalVisible(false)}
              icon={<CloseOutlined />}
              style={{ borderRadius: 10, height: 42, padding: '0 28px', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={editLoading}
              icon={<SaveOutlined />}
              style={{
                borderRadius: 10,
                height: 42,
                padding: '0 28px',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                border: 'none'
              }}
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default UserProfileView;